<template>
  <div>
    <div @click="cancel" class="vm--overlay"></div>
    <div class="create-rental-container">
      <div class="create-rental-title">{{ titleText }}</div>

      <div class="create-rental-schedule-error" v-if="scheduleError">
        <img src="../../assets/error_red.png" />
        <span>{{ errorMessage }}</span>
      </div>

      <div class="create-rental-label-input">
        <label for="full-name">Full Name</label>
        <input
          :class="{ error: !validName }"
          v-model="name"
          placeholder="E.g. John Doe"
          type="text"
          id="full-name"
        />
      </div>
      <div class="create-rental-basic-info-container">
        <div class="create-rental-label-input">
          <label for="phone-number">Phone</label>
          <input
            :class="{ error: !validPhone }"
            v-model="phone"
            placeholder="(00) 0000 - 0000"
            type="phone"
            id="phone-number"
          />
        </div>
        <div class="create-rental-label-input">
          <label for="email">E-mail</label>
          <input
            :class="{ error: !validEmail }"
            v-model="email"
            placeholder="housemate-name@mail.com"
            type="email"
            id="email"
          />
        </div>
      </div>

      <div class="create-rental-dropdowns-container">
        <div class="create-rental-label-dropdown">
          <label for="property-dropdown">Property</label>
          <select
            :class="{ error: !validProperty }"
            v-model="property"
            name="property-dropdown"
            id="property"
          >
            <option selected :value="null">Select Property</option>
            <option
              v-for="property in properties"
              :value="property"
              :key="'property-' + property.uuid"
            >
              {{ property.name }}
            </option>
          </select>
        </div>
        <div class="create-rental-label-dropdown">
          <label for="room-dropdown">Room</label>
          <select
            :class="{ error: !validRoom }"
            v-model="room"
            name="room-dropdown"
            id="room"
          >
            <option selected :value="null">Select Room</option>
            <option
              v-for="room in rooms"
              :value="{ uuid: room.uuid }"
              :key="'room-' + room.uuid"
            >
              {{ room.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="create-rental-details-container">
        <div class="create-rental-label-dropdown">
          <label for="arrival">Arrival</label>
          <DatePicker
            :class="{ error: !validArrival }"
            format="YYYY-MM-DD"
            v-model="arrival"
            name="arrival"
            placeholder="Select Date"
          />
        </div>
        <div class="create-rental-label-dropdown">
          <label for="departure">Departure</label>
          <DatePicker
            @click="selectingDeparture = !selectingDeparture"
            :class="{ error: !validDeparture }"
            format="YYYY-MM-DD"
            v-model="departure"
            name="departure"
            placeholder="Select Date"
          />
        </div>
      </div>

      <div class="create-rental-buttons-container">
        <button @click="cancel()" class="create-rental-cancel-button">
          Cancel
        </button>
        <button @click="submit()" class="create-rental-create-button">
          {{ buttonText }}
        </button>
      </div>
    </div>
    <loader :isShow="isLoading" />
  </div>
</template>

<script>
import calls from '@/api/calls.js'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import '../../assets/css/vue2-datepicker.css'
import Loader from '@/components/Loader.vue'

const logError = (title, error) => {
  console.error(`${title}: ${error.message}`)
}

export default {
  name: 'NewHousematePopup',
  components: {
    DatePicker,
    Loader
  },
  props: {
    selectedRental: null
  },
  data() {
    return {
      name: null,
      phone: null,
      email: null,
      arrival: null,
      departure: null,
      properties: [],
      rooms: [],
      property: null,
      room: null,
      housemate: null,
      originalRental: null,
      rental: null,
      isUpdate: false,
      titleText: null,
      buttonText: null,

      //validators
      scheduleError: false,
      validName: true,
      validEmail: true,
      validPhone: true,
      validProperty: true,
      validRoom: true,
      validArrival: true,
      validDeparture: true,
      errorMessage: 'Departure date should be after arrival date',
      isLoading: false
    }
  },
  methods: {
    cancel() {
      this.$emit('cancel')
    },
    validateHousemate() {
      this.validName = this.name ? true : false
      this.validEmail = this.email ? true : false
      this.validPhone = this.phone ? true : false
      return this.validName && this.validEmail && this.validPhone
    },
    validateRental() {
      this.validProperty = this.property ? true : false
      this.validRoom = this.room ? true : false
      this.validArrival = this.arrival ? true : false
      this.validDeparture = this.departure ? true : false
      return (
        this.validProperty &&
        this.validRoom &&
        this.validArrival &&
        this.validDeparture
      )
    },
    validateSchedule() {
      this.scheduleError = !moment(this.arrival).isBefore(this.departure)
      this.validArrival = this.scheduleError ? false : true
      this.validDeparture = this.scheduleError ? false : true
      return !this.scheduleError
    },
    async deleteHousemate() {
      const { message, data } = await calls.DELETE(
        'housemates',
        this.housemate.uuid
      )
      if (message) throw Error(message)
      return data
    },
    async createHousemate() {
      const { message, data } = await calls.POST('housemates', {
        name: this.name,
        email: this.email,
        phone: this.phone
      })
      if (message) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      } else {
        return data
      }
    },
    async createRental() {
      const { message, data } = await calls.POST(
        `rental/${this.room.uuid}/${this.housemate.uuid}`,
        {
          arrival: moment(this.arrival)
            .utcOffset(this.property.utcoffset)
            .toISOString(),
          departure: moment(this.departure)
            .utcOffset(this.property.utcoffset)
            .toISOString(),
          confirmationCode: 'tbd',
          houseCode: 'tbd',
          channel: 'tbd',
          companions: 0
        }
      )
      if (message) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      } else {
        return data
      }
    },
    async findHousemate() {
      if (this.isUpdate == false) return null
      const { message, data } = await calls.GET(
        'housemates/by/phone',
        this.selectedRental[3].phone
      )
      if (message) throw Error(message)
      return data
    },
    async findRental() {
      const { message, data } = await calls.GET(
        'rental/a',
        `${this.room.uuid}/${this.housemate.uuid}`
      )
      if (message) throw Error(message)
      return data
    },
    async getAllRentals() {
      const { message, data } = await calls.GET(
        'rental/housemates',
        this.room.uuid
      )
      if (message) throw Error(message)
      return data
    },
    async updateHousemate() {
      const { message, data } = await calls.PUT(
        'housemates',
        this.housemate.uuid,
        {
          name: this.name,
          email: this.email,
          phone: this.phone
        }
      )
      if (message) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      } else {
        return data
      }
    },
    async updateRental() {
      {
        //delete de old rental
        //const room = this.originalRental[1]
        const { message } = await calls.DELETE(
          `rental/${this.confirmationCode}`
        )
        if (message) throw Error(message)
      }
      {
        //i know this is repeated code but it for easier debugging and error handling, create a new rental with the new room and stuff
        const { message, data } = await calls.POST(
          `rental/${this.room.uuid}/${this.housemate.uuid}`,
          {
            arrival: moment(this.arrival)
              .utcOffset(this.property.utcoffset)
              .toISOString(),
            departure: moment(this.departure)
              .utcOffset(this.property.utcoffset)
              .toISOString(),
            confirmationCode: 'tbd',
            houseCode: 'tbd',
            channel: 'tbd',
            companions: 0
          }
        )
        if (message) {
          this.$notify({
            group: 'rent-mgt-notification',
            type: 'error',
            text: message
          })
        } else {
          return data
        }
      }
    },
    async loadProperties() {
      const { message, data } = await calls.GET('properties')
      this.properties = data
    },
    async toggleEditMode() {
      this.titleText = 'Edit Housemate'
      this.buttonText = 'Save Changes'
      this.isUpdate = true
      const room = this.rental[1]
      const rental = this.rental[2]
      const housemate = this.rental[3]
      this.name = housemate.name
      this.email = housemate.email
      this.phone = housemate.phone
      const { message, data } = await calls.GET('rooms/property', room.uuid)
      this.property = this.properties.find((e) => e.uuid == data.uuid)
      this.room = {
        uuid: room.uuid
      }
      this.arrival = new Date(rental.arrival)
      this.departure = new Date(rental.departure)
      this.confirmationCode = rental.confirmationCode
      this.channel = rental.channel
      this.numHousemates = rental.companions
    },
    async submit() {
      this.isLoading = true
      try {
        this.validateHousemate()
        this.validateRental()
        this.validateSchedule()
        if (
          this.validateHousemate() &&
          this.validateRental() &&
          this.validateSchedule()
        ) {
          //check if that housemate existst before updating
          let found_housemate = null
          try {
            found_housemate = await this.findHousemate()
          } catch (e) {
            throw e
          }

          //check if thaat room is available
          let room_available = true
          let other_housemate = null
          let all_rentals = null
          try {
            all_rentals = await this.getAllRentals()
          } catch (e) {
            throw e
          }
          if (all_rentals != null) {
            for (let rental of all_rentals) {
              if (
                found_housemate != null &&
                rental[0].uuid == found_housemate.uuid &&
                this.isUpdate
              )
                //updating a housemate's rental
                continue
              if (
                moment(this.arrival).isBefore(
                  moment(rental[1].departure, 'YYYY-MM-DDTHH:mm:ss')
                )
              ) {
                //cant create aa new rental if its occupied
                room_available = false
                other_housemate = rental[0].name
                break
              }
            }
          }

          if (room_available) {
            if (this.isUpdate) {
              if (!found_housemate) {
                return
              }
              //update housemate
              {
                this.housemate = found_housemate
                let res = null
                try {
                  res = await this.updateHousemate()
                } catch (e) {
                  throw e
                }
              }
              //update rental
              // {
              //   let res = null
              //   try {
              //     res = await this.updateRental()
              //   } catch (e) {
              //     logError('updateRental', e)
              //     return
              //   }
              // }
            } else {
              if (!found_housemate) {
                //create housemate
                try {
                  found_housemate = await this.createHousemate()
                } catch (e) {
                  throw e
                }
              }
              //create rental
              this.housemate = found_housemate
              let res = null
              try {
                res = await this.createRental()
              } catch (e) {
                throw e
              }
            }
          } else {
            this.errorMessage = `An existing rental overlaps with this dates`
            this.scheduleError = true
            return
          }

          this.$emit('submitted')
        }
      } catch (err) {
        logError('submit', err)
      } finally {
        // wait 5 seconds after reload the data in submitted event
        setTimeout(() => {
          this.isLoading = false
        }, 5000)
      }
    }
  },
  watch: {
    property: {
      async handler() {
        if (this.property) {
          await this.$nextTick()
          const { message, data } = await calls.GET(
            'properties/rooms',
            this.property.uuid
          )
          this.rooms = data || []
        } else {
          this.rooms = []
        }
      },
      immediate: false
    }
  },
  async mounted() {
    this.$emit('loading', true)
    try {
      await this.loadProperties()
      this.titleText = 'Create Housemate'
      this.buttonText = 'Create Housemate'
      if (this.selectedRental != null) {
        this.rental = this.selectedRental
        this.originalRental = this.selectedRental
        await this.toggleEditMode()
      }
    } finally {
      this.$emit('loading', false)
    }
  }
}
</script>

<style scoped>
.create-rental-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 12;
}

.create-rental-container {
  position: absolute;
  top: 10%;
  left: calc(50% - 254px);
  display: flex;
  flex-flow: column nowrap;
  margin: 0;
  padding-right: 40px;
  border-radius: 4px;
}

.create-rental-container .mx-datepicker .mx-input {
  cursor: pointer;
  font-family: 'Montserrat';
  color: #2c3e50;
  font-size: 13px;
}

.create-rental-container .mx-datepicker .mx-input ::placeholder {
  color: #2c3e50;
  opacity: 1;
}

.create-rental-container .mx-datepicker .mx-input ::-ms-input-placeholder {
  color: #2c3e50;
}

.create-rental-title {
  font-family: Montserrat;
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 22px;
  color: #66b015;
  margin-bottom: 20px;
}

.create-rental-basic-info-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
}

.create-rental-basic-info-container div.create-rental-label-input {
  width: 212px;
}

.create-rental-label-input {
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  height: 64px;
  padding: 12px 0 12px 0;
}

.create-rental-label-input label {
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  color: #4a4a4a;
}
.create-rental-label-input input {
  width: 100%;
  height: 40px;
  margin-top: 5px;
  border: 1px solid #cbd4dc;
  box-sizing: border-box;
  border-radius: 5px;
  padding: 0 8px 0 8px;
  font-size: 13px;
}

.create-rental-dropdowns-container {
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.create-rental-label-dropdown {
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  padding: 12px 0 12px 0;
  width: 100%;
  height: 64px;
}

.create-rental-label-dropdown label {
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  color: #4a4a4a;
}

.create-rental-details-container {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  position: relative;
  width: 100%;
}
.create-rental-details-container .create-rental-label-dropdown {
  width: 212px;
}

.create-rental-buttons-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: baseline;
  padding-top: 10px;
  width: 100%;
}

.create-rental-cancel-button,
.create-rental-create-button {
  font-family: Montserrat;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: left;
  border-radius: 4px;
  width: 176px;
  height: 48px;
  text-align: center;
}

.create-rental-cancel-button:hover,
.create-rental-create-button:hover {
  cursor: pointer;
}

.create-rental-cancel-button {
  border: 1px solid #767675;
  background: #ffffff;
  color: #767675;
}

.create-rental-create-button {
  border: none;
  background: #66b015;
  color: white;
  margin-left: 24px;
}

.create-rental-schedule-error {
  display: flex;
  flex-flow: row nowrap;
  padding: 12px;
  width: 100%;
  color: red;
  background: rgba(223, 0, 15, 0.05);
  border-radius: 6px;
}

.create-rental-schedule-error span {
  margin-left: 12px;
  color: #df000f;
  font-family: Montserrat;
  font-weight: 900;
  font-size: 13px;
  line-height: 18px;
}

div.mx-datepicker {
  cursor: pointer;
}

input.error,
select.error {
  border-color: red;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .create-rental-container {
    position: fixed;
    top: 5%;
    left: 5%;
    right: 5%;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    padding: 20px;
    transform: none;
  }

  .create-rental-title {
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 16px;
  }

  .create-rental-basic-info-container {
    flex-direction: column;
    gap: 0;
  }

  .create-rental-basic-info-container div.create-rental-label-input {
    width: 100%;
  }

  .create-rental-label-input {
    height: auto;
    padding: 8px 0;
  }

  .create-rental-label-input input {
    height: 44px;
    font-size: 16px;
    margin-top: 8px;
  }

  .create-rental-dropdowns-container {
    gap: 0;
  }

  .create-rental-label-dropdown {
    height: auto;
    padding: 8px 0;
  }

  .create-rental-label-dropdown select {
    height: 44px;
    font-size: 16px;
    margin-top: 8px;
  }

  .create-rental-details-container {
    flex-direction: column;
    gap: 0;
  }

  .create-rental-details-container .create-rental-label-dropdown {
    width: 100%;
  }

  .create-rental-buttons-container {
    flex-direction: column;
    gap: 12px;
    padding-top: 20px;
  }

  .create-rental-cancel-button,
  .create-rental-create-button {
    width: 100%;
    height: 44px;
    font-size: 16px;
    margin-left: 0;
  }

  .create-rental-schedule-error {
    padding: 16px;
    margin-bottom: 16px;
  }

  .create-rental-schedule-error span {
    font-size: 14px;
    line-height: 20px;
  }
}

@media (max-width: 480px) {
  .create-rental-container {
    top: 2%;
    left: 2%;
    right: 2%;
    width: 96%;
    padding: 16px;
  }

  .create-rental-title {
    font-size: 16px;
    line-height: 22px;
  }

  .create-rental-label-input,
  .create-rental-label-dropdown {
    padding: 6px 0;
  }

  .create-rental-label-input input,
  .create-rental-label-dropdown select {
    height: 40px;
    font-size: 16px;
  }

  .create-rental-buttons-container {
    gap: 8px;
  }
}
</style>
